import { Fragment, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Typography } from '@components/Typography';
import Autocomplete from '@components/Autocomplete';
import { MultiSelect } from '@components/MultiSelect';
import { useTranslation, useLocations } from '@hooks';
import { ROLE, ACCESS_LEVEL } from '@constants/user';

const Wrapper = styled('div')({
    display: 'flex',
    gap: 24,
    marginTop: -8,
    marginBottom: 8,
    '@media (max-width: 768px)': {
        marginTop: -16,
    },
});

const StyledRadioGroup = styled(RadioGroup)({
    flexDirection: 'row',
    alignItems: 'center',
    '& .MuiFormControlLabel-root': {
        marginRight: 0,
        marginLeft: '-3px !important',
    },
});

export const LOCATION_TYPE = {
    LOCATIONS: '0',
    BRANDS: '1',
};

const ComboLocationMultiselect = ({
    brandsOptions,
    locationsOptions,
    data,
    disabled,
    isView,
    updatedLocationsCb = () => {},
}) => {
    const user = useSelector(state => state.auth.user);
    const brands = useSelector(state => state.brands.data);
    const { control, watch, setValue } = useFormContext();
    const { t } = useTranslation();
    const { mapSelectedLocationsToFormValue } = useLocations();

    const locationType = watch('locationType');
    const brandAccess = watch('brandAccess');

    useEffect(() => {
        const hasRestrictedAccess =
            (user?.role === ROLE.USER || user?.role === ROLE.ADMIN) && user?.accessLevel === ACCESS_LEVEL.LOCATION;
        setValue('brandAccess', !hasRestrictedAccess, { shouldDirty: true });
        if (hasRestrictedAccess && !isView) {
            setValue('locationType', LOCATION_TYPE.LOCATIONS, { shouldValidate: true });
        }

        if (data) {
            setValue('brands', data?.brands, { shouldValidate: true });
            setValue('locations', mapSelectedLocationsToFormValue(data?.locations), {
                shouldValidate: true,
            });
            setValue('locationType', data?.locations.length ? LOCATION_TYPE.LOCATIONS : LOCATION_TYPE.BRANDS, {
                shouldValidate: true,
            });
        }
    }, [setValue, user, data]);

    return brandAccess || isView ? (
        <div className="combo-location-multiselect">
            <Controller
                control={control}
                name="locationType"
                render={() => (
                    <Wrapper className="location-type">
                        {[
                            { id: LOCATION_TYPE.BRANDS, name: t('common.brands') },
                            { id: LOCATION_TYPE.LOCATIONS, name: t('common.locations') },
                        ].map(type => (
                            <Fragment key={type.id}>
                                <StyledRadioGroup
                                    value={locationType}
                                    onChange={e => {
                                        setValue('locationType', e.target.value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                >
                                    <FormControlLabel
                                        value={type.id}
                                        control={
                                            <Radio
                                                sx={{
                                                    color: 'rgba(0, 0, 0, 0.6)',
                                                    padding: '6px',
                                                    '&.Mui-checked': { color: '#1F4C5C' },
                                                }}
                                                disabled={disabled}
                                            />
                                        }
                                        label={
                                            <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                {type.name}
                                            </Typography>
                                        }
                                        disabled={disabled}
                                    ></FormControlLabel>
                                </StyledRadioGroup>
                            </Fragment>
                        ))}
                    </Wrapper>
                )}
            />
            {locationType === LOCATION_TYPE.BRANDS && (
                <Controller
                    control={control}
                    name="brands"
                    render={({ field, fieldState: { error }, ref }) => (
                        <MultiSelect
                            {...field}
                            ref={ref}
                            onChange={value => {
                                setValue('brands', value, {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                setValue('locations', [], { shouldDirty: true, shouldValidate: true });
                                const availableCompanies = brands
                                    .filter(item => value.includes(item.id))
                                    .map(item => item.locations)
                                    .flat()
                                    .map(item => item.id);
                                updatedLocationsCb(availableCompanies);
                                setValue('selectedLocationsIds', availableCompanies);
                            }}
                            options={brandsOptions}
                            label={t('common.brands')}
                            fullWidth
                            countable
                            required
                            error={error}
                            disabled={disabled}
                        />
                    )}
                />
            )}
            {locationType === LOCATION_TYPE.LOCATIONS && (
                <Controller
                    control={control}
                    name="locations"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Autocomplete
                            {...field}
                            ref={ref}
                            label={t('common.locations')}
                            options={locationsOptions}
                            value={field.value}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            inputProps={{
                                style: {
                                    display: field?.value?.length > 0 ? 'none' : 'block',
                                },
                            }}
                            onChangeAutoComplete={items => {
                                setValue('locations', items.length ? items : [], {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                setValue('brands', [], { shouldDirty: true, shouldValidate: true });
                                updatedLocationsCb(items.map(i => i.id));
                                setValue(
                                    'selectedLocationsIds',
                                    items.map(i => i.id),
                                );
                            }}
                            groupMultiProps={{
                                getOptionLabel: option => option.label,
                            }}
                            required
                            error={error}
                            disabled={disabled}
                        />
                    )}
                />
            )}
        </div>
    ) : (
        <div className="combo-location-multiselect">
            <Controller
                control={control}
                name="locations"
                render={({ field, fieldState: { error }, ref }) => (
                    <Autocomplete
                        {...field}
                        ref={ref}
                        label={t('common.locations')}
                        options={locationsOptions}
                        value={field.value}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        inputProps={{
                            style: {
                                display: field?.value?.length > 0 ? 'none' : 'block',
                            },
                        }}
                        onChangeAutoComplete={items => {
                            setValue('locations', items.length ? items : [], {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                            setValue('brands', [], { shouldDirty: true, shouldValidate: true });
                            updatedLocationsCb(items.map(i => i.id));
                            setValue(
                                'selectedLocationsIds',
                                items.map(i => i.id),
                            );
                        }}
                        groupMultiProps={{
                            getOptionLabel: option => option.label,
                        }}
                        required
                        error={error}
                        disabled={disabled}
                    />
                )}
            />
        </div>
    );
};

export default ComboLocationMultiselect;
